/**
 * RTK Query endpoints — Tags
 *
 * Tag CRUD + autocomplete. Tags are tenant-scoped at the DB schema level
 * so name uniqueness is per-tenant. `deleteTag` is a soft-delete that
 * flips `active` to false — historical references stay navigable.
 *
 * Usage:
 *   const { data } = useTagListQuery({ filters: { includeInactive: false } });
 *   const [createTag] = useCreateTagMutation();
 *   const { data: matches } = useTagAutocompleteQuery({ q: 'meca' });
 */

import { baseApi } from './baseApi';
import { buildQueryString } from './utils';

const tagApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // GET /su/tag — paginated list
    tagList: builder.query({
      query: (params = {}) => ({
        url: `/su/tag${buildQueryString(params)}`,
        method: 'GET',
      }),
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map(({ id }) => ({ type: 'Tag', id })),
              { type: 'Tag', id: 'LIST' },
            ]
          : [{ type: 'Tag', id: 'LIST' }],
    }),

    // GET /su/tag/name-autocomplete — typeahead used by the multi-select
    tagAutocomplete: builder.query({
      query: ({ q = '', limit = 20 } = {}) => ({
        url: '/su/tag/name-autocomplete',
        method: 'GET',
        params: { q, limit },
      }),
      providesTags: [{ type: 'Tag', id: 'AUTOCOMPLETE' }],
      keepUnusedDataFor: 60,
    }),

    // GET /su/tag/:id
    tag: builder.query({
      query: (id) => ({ url: `/su/tag/${id}`, method: 'GET' }),
      providesTags: (_r, _e, id) => [{ type: 'Tag', id }],
    }),

    // POST /su/tag
    createTag: builder.mutation({
      query: (body) => ({ url: '/su/tag', method: 'POST', body }),
      invalidatesTags: [
        { type: 'Tag', id: 'LIST' },
        { type: 'Tag', id: 'AUTOCOMPLETE' },
      ],
    }),

    // PUT /su/tag/:id
    editTag: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/su/tag/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Tag', id },
        { type: 'Tag', id: 'LIST' },
        { type: 'Tag', id: 'AUTOCOMPLETE' },
      ],
    }),

    // DELETE /su/tag/:id (soft-delete: active=false)
    deleteTag: builder.mutation({
      query: (id) => ({ url: `/su/tag/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'Tag', id },
        { type: 'Tag', id: 'LIST' },
        { type: 'Tag', id: 'AUTOCOMPLETE' },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useTagListQuery,
  useTagAutocompleteQuery,
  useLazyTagAutocompleteQuery,
  useTagQuery,
  useCreateTagMutation,
  useEditTagMutation,
  useDeleteTagMutation,
} = tagApi;

export default tagApi;
